import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { APP_MODES } from "../utils/appMode";
import { AUTH_CHANGED_EVENT } from "../context/CartContext";
import { markJustLoggedOut } from "../utils/biometricLogin";
import { clearRememberedRedirect } from "../utils/authRedirect";
import { unregisterPushTokenOnLogout } from "../notifications/push";
import "./Header.css";

function Icon({ name, active = false, className = "" }) {
  const cls = `stir-icon ${active ? "is-active" : ""} ${className}`.trim();

  switch (name) {
    case "chart":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <path
            d="M5 19V6M12 19V10M19 19V4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    case "orders":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <rect
            x="4"
            y="5"
            width="16"
            height="15"
            rx="3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M8 3.8v3M16 3.8v3M7.5 10.5h9M7.5 14h6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    case "users":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <circle
            cx="9"
            cy="8"
            r="3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <circle
            cx="17"
            cy="9"
            r="2.3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M4.5 19c.8-2.8 2.9-4.2 6-4.2 3.1 0 5.2 1.4 6 4.2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            d="M16.2 15.4c1.8.2 3.1 1 3.8 2.6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    case "store":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <path
            d="M4 10h16l-1.2 9H5.2z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M6 10 7.2 5h9.6L18 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "gift":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <path
            d="M4 10h16v10H4z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M3 10h18v-3.5A1.5 1.5 0 0 0 19.5 5H4.5A1.5 1.5 0 0 0 3 6.5V10z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M12 5v15M8.2 5c-1.3 0-2.2-.8-2.2-1.9 0-1 .8-1.8 1.8-1.8C10 1.3 12 5 12 5M15.8 5c1.3 0 2.2-.8 2.2-1.9 0-1-.8-1.8-1.8-1.8C14 1.3 12 5 12 5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "table":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <rect
            x="4"
            y="5"
            width="16"
            height="14"
            rx="2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M4 10h16M10 5v14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
        </svg>
      );
    case "layout":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <rect
            x="4"
            y="4"
            width="16"
            height="16"
            rx="2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M4 9h16M4 15h16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
        </svg>
      );
    case "user":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <circle
            cx="12"
            cy="8"
            r="3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M5 20c.9-3.4 3.5-5 7-5s6.1 1.6 7 5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    case "logout":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <path
            d="M10 17l-5-5 5-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5 12h10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            d="M14 5h3a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    case "chevron":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <path
            d="M7 10l5 5 5-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    default:
      return null;
  }
}

/* =========================================================
   Stato attivo del menu admin — helper unico, riusato da TUTTE
   le voci (nav bar in alto, pannello account/mobile, singole e
   gruppi padre/figli).
   ========================================================= */

// Un "to" può portare una query string (es. /admin/ordini?page=1): per il
// match conta solo il path, mai la query.
function stripQueryAndHash(to) {
  return String(to || "").split("?")[0].split("#")[0];
}

// Confronto per segmenti di path: attivo sulla route esatta, sulle sue
// sottopagine (/admin/ordini/123) e con qualsiasi query string, MAI su un
// path "vicino" per prefisso di stringa (/admin/ordini-qualcosa).
function pathMatches(pathname, to) {
  const target = stripQueryAndHash(to);
  if (!target) return false;
  return pathname === target || pathname.startsWith(`${target}/`);
}

// Tra tutte le voci di uno stesso menu che matchano la pathname corrente,
// tiene attive solo quelle con il path più specifico (più lungo): evita che
// una voce "radice" (es. /admin) resti accesa insieme a una voce più
// profonda (es. /admin/ordini). A parità di specificità (path identici,
// es. due voci diverse che puntano alla stessa pagina) restano attive
// entrambe: non è un conflitto, sono davvero sulla stessa route. Il Set
// risultante contiene i path già "spogliati" di query/hash: va confrontato
// con isPathActive(), non con un "to" grezzo.
function mostSpecificActivePaths(pathname, candidatePaths) {
  const targets = candidatePaths.map(stripQueryAndHash);
  const matched = targets.filter((target) => pathMatches(pathname, target));
  if (!matched.length) return new Set();
  const maxLen = Math.max(...matched.map((t) => t.length));
  return new Set(matched.filter((t) => t.length === maxLen));
}

function isPathActive(activePaths, to) {
  return activePaths.has(stripQueryAndHash(to));
}

// Struttura del pannello account/mobile: singola sorgente di verità usata
// sia per il rendering sia per il calcolo dello stato attivo, così nessuna
// voce porta con sé una logica di match propria.
const ADMIN_MENU_SECTIONS = [
  { type: "link", to: "/admin/statistiche", label: "Statistiche", icon: "chart" },
  { type: "link", to: "/admin", label: "Immagini Home", icon: "chart" },
  {
    type: "link",
    to: "/admin/header-footer",
    label: "Header & Footer",
    icon: "layout",
  },
  {
    type: "group",
    key: "clients",
    title: "Clienti",
    icon: "users",
    children: [
      { to: "/admin/clienti", label: "Clienti", icon: "users" },
      { to: "/admin/organizzazioni", label: "Organizzazioni", icon: "users" },
      { to: "/admin/ordini", label: "Ordini Clienti", icon: "orders" },
    ],
  },
  {
    type: "group",
    key: "suppliers",
    title: "Fornitori",
    icon: "store",
    children: [
      { to: "/admin/fornitori", label: "Fornitori", icon: "store" },
      { to: "/admin/approvals", label: "Approva Fornitori", icon: "users" },
      {
        to: "/admin/zones",
        label: "Tabella Comuni/Fornitore",
        icon: "table",
      },
    ],
  },
  {
    type: "group",
    key: "promos",
    title: "Promozioni",
    icon: "gift",
    children: [
      { to: "/admin/promozioni", label: "Gestione promozioni", icon: "gift" },
      {
        to: "/admin/promozioni/clienti",
        label: "Promo Clienti",
        icon: "users",
      },
      {
        to: "/admin/promozioni/segmenti",
        label: "Ultima promo",
        icon: "chart",
      },
    ],
  },
  {
    type: "link",
    to: "/admin/wallet-topup-offers",
    label: "Offerte ricarica",
    icon: "gift",
  },
  {
    type: "link",
    to: "/admin/gdpr",
    label: "Richieste GDPR",
    icon: "user",
  },
  {
    type: "group",
    key: "subscriptions",
    title: "Abbonamenti",
    icon: "gift",
    children: [
      { to: "/admin/abbonamenti/piani", label: "Piani", icon: "gift" },
      {
        to: "/admin/abbonamenti/richieste",
        label: "Richieste Fornitori",
        icon: "users",
      },
      {
        to: "/admin/abbonamenti/sottoscrizioni",
        label: "Sottoscrizioni",
        icon: "orders",
      },
    ],
  },
  {
    type: "link",
    to: "/admin/contabilita/percentuali",
    label: "Percentuali",
    icon: "table",
  },
  {
    type: "group",
    key: "orders",
    title: "Ordini",
    icon: "orders",
    children: [
      {
        to: "/admin/contabilita/ordini",
        label: "Contabilità Ordini",
        icon: "table",
      },
      { to: "/admin/ordini?page=1", label: "Ordini", icon: "orders" },
    ],
  },
  {
    type: "group",
    key: "invoices",
    title: "Fatture",
    icon: "table",
    children: [
      {
        to: "/admin/contabilita/fatturazione",
        label: "Fatturazione ordini",
        icon: "table",
      },
      {
        to: "/admin/contabilita/abbonamenti",
        label: "Fatturazione abbonamenti",
        icon: "gift",
      },
      {
        to: "/admin/contabilita/fatture",
        label: "Fatture Emesse",
        icon: "table",
      },
      {
        to: "/admin/contabilita/pagamentifornitori",
        label: "Pagamenti Fornitori",
        icon: "table",
      },
    ],
  },
  {
    type: "group",
    key: "articles",
    title: "Articoli",
    icon: "table",
    children: [
      { to: "/admin/servizi", label: "Tabella Articoli", icon: "table" },
      {
        to: "/admin/cross-sell",
        label: "Xchè non Agg/Hai Dim Qualcosa",
        icon: "table",
      },
    ],
  },
];

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 1024px)").matches
      : true,
  );

  useEffect(() => {
    const mql = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => setIsDesktop(e.matches);

    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }

    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return isDesktop;
}

export default function AdminHeader({ user, mode, onChangeMode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useIsDesktop();

  const [accountOpen, setAccountOpen] = useState(false);
  // Un solo stato per tutti i gruppi padre/figli (chiave = section.key in
  // ADMIN_MENU_SECTIONS), invece di un useState per gruppo.
  const [openGroups, setOpenGroups] = useState({});

  const setGroupOpen = (key) => (updater) =>
    setOpenGroups((prev) => ({
      ...prev,
      [key]: typeof updater === "function" ? updater(!!prev[key]) : updater,
    }));

  const accountRef = useRef(null);
  const desktopRef = useRef(null);
  const mobileRef = useRef(null);
  const accountScrollRef = useRef(null);

  const closeMenus = () => {
    setAccountOpen(false);
    setOpenGroups({});
  };

  const handleLogout = () => {
    // Senza await: legge il JWT ora, il logout non aspetta la rete.
    unregisterPushTokenOnLogout();
    localStorage.removeItem("token");
    clearRememberedRedirect(); // niente "pagina richiesta" residua
    // Logout esplicito: sospende SOLO l'auto-trigger biometrico al prossimo
    // arrivo su LoginPage (il bottone FaceID/impronta manuale resta attivo).
    markJustLoggedOut();
    window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
    closeMenus();
    navigate("/login");
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        closeMenus();
      }
    };

    const onKey = (e) => {
      if (e.key === "Escape") closeMenus();
    };

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    const applySpaces = () => {
      const docEl = document.documentElement;
      const desktopH = desktopRef.current
        ? Math.ceil(desktopRef.current.getBoundingClientRect().height)
        : 0;
      const mobileH = mobileRef.current
        ? Math.ceil(mobileRef.current.getBoundingClientRect().height)
        : 0;

      docEl.style.setProperty(
        "--stir-header-top-space",
        isDesktop ? `${desktopH + 24}px` : "0px",
      );
      docEl.style.setProperty(
        "--stir-header-bottom-space",
        !isDesktop ? `${mobileH + 10}px` : "0px",
      );
    };

    applySpaces();

    const ro = new ResizeObserver(applySpaces);
    if (desktopRef.current) ro.observe(desktopRef.current);
    if (mobileRef.current) ro.observe(mobileRef.current);

    window.addEventListener("resize", applySpaces);
    window.addEventListener("orientationchange", applySpaces);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", applySpaces);
      window.removeEventListener("orientationchange", applySpaces);
    };
  }, [isDesktop, accountOpen, openGroups]);

  useEffect(() => {
    if (isDesktop) {
      document.body.style.overflow = "";
      return;
    }

    document.body.style.overflow = accountOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [accountOpen, isDesktop]);

  const adminItems = useMemo(() => {
    return [
      { key: "dashboard", label: "Dashboard", to: "/admin", icon: "chart" },
      { key: "orders", label: "Ordini", to: "/admin/ordini", icon: "orders" },
      { key: "clients", label: "Clienti", to: "/admin/clienti", icon: "users" },
      {
        key: "suppliers",
        label: "Fornitori",
        to: "/admin/fornitori",
        icon: "store",
      },
      { key: "statistics", label: "Statistiche", to: "/admin/statistiche", icon: "chart" },
      { key: "promos", label: "Promo", to: "/admin/promozioni", icon: "gift" },
      {
        key: "accounting",
        label: "Contabilità",
        to: "/admin/contabilita/ordini",
        icon: "table",
      },
      {
        key: "site-nav",
        label: "Header & Footer",
        to: "/admin/header-footer",
        icon: "layout",
      },
    ];
  }, []);

  // Stato attivo centralizzato: un unico calcolo per scope di menu (nav bar
  // in alto vs. pannello account/mobile), condiviso da voci singole e da
  // tutti i figli dei gruppi padre/figli.
  const topNavActivePaths = useMemo(
    () =>
      mostSpecificActivePaths(
        location.pathname,
        adminItems.map((item) => item.to),
      ),
    [location.pathname, adminItems],
  );

  const accountMenuPaths = useMemo(
    () =>
      ADMIN_MENU_SECTIONS.flatMap((section) =>
        section.type === "group"
          ? section.children.map((child) => child.to)
          : [section.to],
      ),
    [],
  );

  const accountMenuActivePaths = useMemo(
    () => mostSpecificActivePaths(location.pathname, accountMenuPaths),
    [location.pathname, accountMenuPaths],
  );

  const renderNavItem = (item, mobile = false) => {
    const active = item.to
      ? isPathActive(topNavActivePaths, item.to)
      : false;

    return (
      <Link
        to={item.to}
        className={`stir-nav-link ${mobile ? "is-mobile" : ""}`}
        aria-current={active ? "page" : undefined}
        onClick={closeMenus}
      >
        <span className={`stir-nav-icon-wrap ${active ? "is-active" : ""}`}>
          <Icon name={item.icon} active={active} />
        </span>
        <span className={`stir-nav-label ${active ? "is-active" : ""}`}>
          {item.label}
        </span>
      </Link>
    );
  };

  const AccountContent = ({ mobile = false }) => (
    <div className={`stir-account-panel ${mobile ? "is-mobile" : ""}`}>
      <div className="stir-account-head stir-account-head-admin">
        <div className="stir-account-mode-copy">
          <div className="stir-account-title">Modalità attuale</div>
          <div className="stir-account-subtitle">Admin</div>
        </div>

        <div className="stir-account-head-actions">
          {typeof onChangeMode === "function" && (
            <button
              type="button"
              className="stir-mode-box-btn"
              onClick={() => {
                closeMenus();
                onChangeMode(APP_MODES.CUSTOMER);
              }}
            >
              Vai alla vista cliente
            </button>
          )}

          {mobile && (
            <button
              type="button"
              className="stir-sheet-close"
              onClick={closeMenus}
              aria-label="Chiudi menu admin"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="stir-account-scroll" ref={accountScrollRef}>
        <div className="stir-account-list">
          {ADMIN_MENU_SECTIONS.map((section) => {
            if (section.type === "link") {
              return (
                <MenuLink
                  key={section.to}
                  to={section.to}
                  label={section.label}
                  icon={section.icon}
                  onClick={closeMenus}
                  active={isPathActive(accountMenuActivePaths, section.to)}
                />
              );
            }

            const groupActive = section.children.some((child) =>
              isPathActive(accountMenuActivePaths, child.to),
            );

            return (
              <AccordionMenuSection
                key={section.key}
                title={section.title}
                icon={section.icon}
                isOpen={!!openGroups[section.key] || groupActive}
                onToggle={setGroupOpen(section.key)}
                active={groupActive}
                scrollRef={accountScrollRef}
              >
                {section.children.map((child) => (
                  <MenuLink
                    key={child.to}
                    to={child.to}
                    label={child.label}
                    icon={child.icon}
                    onClick={closeMenus}
                    active={isPathActive(accountMenuActivePaths, child.to)}
                  />
                ))}
              </AccordionMenuSection>
            );
          })}

          <button
            type="button"
            className="stir-menu-row is-danger"
            onClick={handleLogout}
          >
            <span className="stir-menu-row-left">
              <Icon name="logout" />
              <span>Logout</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isDesktop ? (
        <header
          className="stir-header-desktop stir-header-admin"
          ref={desktopRef}
        >
          <div className="stir-header-shell">
            <a
              href="https://www.stirup.it"
              target="_blank"
              rel="noreferrer"
              className="stir-brand"
            >
              <div className="stir-brand-mark">
                <img
                  src="/logo.png"
                  alt="Stirup logo"
                  className="stir-brand-logo"
                  width={40}
                  height={40}
                />
              </div>

              <div className="stir-brand-copy">
                <div className="stir-brand-title">Stirup Admin</div>
                <div className="stir-brand-subtitle">Gestione operativa</div>
              </div>
            </a>

            <nav className="stir-desktop-nav" aria-label="Navigazione admin">
              {adminItems.map((item) => (
                <div key={item.key} className="stir-desktop-nav-item">
                  {renderNavItem(item)}
                </div>
              ))}
            </nav>

            <div className="stir-desktop-actions" ref={accountRef}>
              <button
                type="button"
                className={`stir-account-trigger ${accountOpen ? "is-open" : ""
                  }`}
                onClick={() => setAccountOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={accountOpen}
              >
                <span className="stir-account-avatar">
                  <Icon name="user" />
                </span>
                <span className="stir-account-trigger-copy">
                  <span className="stir-account-trigger-title">Admin</span>
                  <span className="stir-account-trigger-subtitle">
                    Pannello
                  </span>
                </span>
                <Icon
                  name="chevron"
                  className={`stir-trigger-chevron ${accountOpen ? "is-open" : ""
                    }`}
                />
              </button>

              {accountOpen && (
                <div className="stir-account-dropdown" role="menu">
                  <AccountContent />
                </div>
              )}
            </div>
          </div>
        </header>
      ) : (
        <>
          <nav
            className="stir-mobile-nav stir-mobile-nav-admin"
            ref={mobileRef}
            aria-label="Navigazione mobile admin"
          >
            <div className="stir-mobile-nav-inner" ref={accountRef}>
              {adminItems.slice(0, 4).map((item) => (
                <div key={item.key} className="stir-mobile-nav-item">
                  {renderNavItem(item, true)}
                </div>
              ))}

              <div className="stir-mobile-nav-item">
                <button
                  type="button"
                  className="stir-nav-link is-mobile"
                  onClick={() => setAccountOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={accountOpen}
                >
                  <span
                    className={`stir-nav-icon-wrap ${accountOpen ? "is-active" : ""
                      }`}
                  >
                    <Icon name="user" active={accountOpen} />
                  </span>
                  <span
                    className={`stir-nav-label ${accountOpen ? "is-active" : ""
                      }`}
                  >
                    Admin
                  </span>
                </button>
              </div>
            </div>
          </nav>

          {accountOpen && (
            <div
              className="stir-sheet-backdrop"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) closeMenus();
              }}
              onTouchStart={(e) => {
                if (e.target === e.currentTarget) closeMenus();
              }}
            >
              <div
                className="stir-sheet"
                role="menu"
                aria-label="Menu admin"
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                <div className="stir-sheet-handle" aria-hidden="true" />
                <AccountContent mobile />
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

function MenuLink({ to, label, icon, onClick, showArrow = false, active = false }) {
  return (
    <Link
      to={to}
      className={`stir-menu-row ${active ? "is-active" : ""}`}
      aria-current={active ? "page" : undefined}
      onClick={onClick}
    >
      <span className="stir-menu-row-left">
        <Icon name={icon} active={active} />
        <span>{label}</span>
      </span>
      {showArrow ? <span className="stir-menu-row-arrow">›</span> : null}
    </Link>
  );
}

function AccordionMenuSection({
  title,
  icon,
  isOpen,
  onToggle,
  active = false,
  children,
}) {
  const triggerRef = useRef(null);

  const handleToggle = () => {
    const el = triggerRef.current;
    const prevTop = el?.getBoundingClientRect().top;

    onToggle((v) => !v);

    requestAnimationFrame(() => {
      if (!el) return;

      const newTop = el.getBoundingClientRect().top;
      const delta = newTop - prevTop;

      if (Math.abs(delta) > 1) {
        const scrollContainer = el.closest(".stir-account-scroll");
        if (scrollContainer) {
          scrollContainer.scrollTop += delta;
        }
      }
    });
  };

  return (
    <div className={`stir-accordion ${isOpen ? "is-open" : ""}`}>
      <button
        ref={triggerRef}
        type="button"
        className={`stir-menu-row stir-accordion-trigger ${isOpen ? "is-open" : ""
          } ${active ? "is-active" : ""}`}
        onClick={handleToggle}
        aria-expanded={isOpen}
      >
        <span className="stir-menu-row-left">
          <span className="stir-accordion-icon-wrap">
            <Icon name={icon} active={active} />
          </span>
          <span className="stir-accordion-title-wrap">
            <span className="stir-accordion-title">{title}</span>
          </span>
        </span>
        <span className={`stir-menu-row-arrow ${isOpen ? "is-open" : ""}`}>
          ›
        </span>
      </button>

      <div className={`stir-accordion-panel ${isOpen ? "is-open" : ""}`}>
        {children}
      </div>
    </div>
  );
}
